import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { catchError, map } from 'rxjs/operators';
import { IHAStatic } from '../interfaces/IHAStatic';
import { IHAChange } from '../interfaces/IHAChange';
import { IHAChangeCandidate } from '../interfaces/IHAChangeCandidate';
import { IHAChangeDistrict } from '../interfaces/IHAChangeDistrict';
import { IHAChangePollingCandidate } from '../interfaces/IHAChangePollingCandidate';
import { IHAChangePollingPlace } from '../interfaces/IHAChangePollingPlace';
import { IAbsentOrdinary } from '../interfaces/IAbsentOrdinary';
import { ICandidateVote, IDeclaration } from '../interfaces/IDeclaration';
import { IHAStaticCandidate } from '../interfaces/IHAStaticCandidate';
import { IHAStaticDistrict } from '../interfaces/IHAStaticDistrict';
import { IHAStaticPollingPlace } from '../interfaces/IHAStaticPollingPlace';
import { ElectionStatusEnum } from '../enums/ElectionStatusEnum';
import { _MatLegacyTableDataSource as _MatTableDataSource } from '@angular/material/legacy-table';
import { IElectionDate } from '../interfaces/IElectionDate';
import { ILCChange } from '../interfaces/ILCChange';
import { ILCChangeParty } from '../interfaces/ILCChangeParty';
import { ILCStaticParty } from '../interfaces/ILCStaticParty';
import { ILCStatic } from '../interfaces/ILCStatic';
import { IDistributionVote } from '../interfaces/IDistributionVote';
import { IFinalDistributionRound } from '../interfaces/IFinalDistributionRound';
import { ILGEChange } from '../interfaces/ILGEChange';
import { ILGEChangeCandidate } from '../interfaces/ILGEChangeCandidate';
import { ILGEChangeElection } from '../interfaces/ILGEChangeElection';
import { ILGEChangeCouncil } from '../interfaces/ILGEChangeCouncil';
import { ILGEDocument } from '../interfaces/ILGEDocument';
import { ILGEStatic } from '../interfaces/ILGEStatic';
import { ILGEStaticCouncil } from '../interfaces/ILGEStaticCouncil';
import { ILGEStaticElection } from '../interfaces/ILGEStaticElection';
import { ILGEStaticCandidate } from '../interfaces/ILGEStaticCandidate';
import { LGEElectionStatusEnum } from '../enums/LGEElectionStatusEnum';
import { LGEResultsStatusEnum } from '../enums/LGEResultsStatusEnum';
import { ILGEEvent } from '../interfaces/ILGEEvent';
import { ILCChangeCandidate } from '../interfaces/ILCChangeCandidate';
import { ILCChangePreviousElectionResult } from '../interfaces/ILCChangePreviousElectionResult';
import { LcFinalResultsService } from './lc-final-results.service';

@Injectable({
  providedIn: 'root',
})
export class ApiDataService {
  constructor(
    private http: HttpClient,
    private lcFinalResultsService: LcFinalResultsService
  ) {}

  // -------------------------- Helpers ----------------------------

  private getCurrent$(method: string): Observable<any> {
    // console.log(environment.currentData + method);
    return this.http.get(environment.currentData + method);
  }

  // ---------------------- ElectionDates ------------------------

  public loadElectionDates$(): Observable<IElectionDate[]> {
    if (environment.isMock) {
      return this.http
        .get('assets/mock/ElectionDates.json')
        .pipe(
          map((response: any) =>
            response.elections.map((item: any) => this.mapToIElectionDate(item))
          )
        );
    }
    return this.getCurrent$(`ElectionDates`).pipe(
      map((response: any) =>
        response.elections.map((item: any) => this.mapToIElectionDate(item))
      )
    );
  }

  private mapToIElectionDate(data: any): IElectionDate {
    const electionStatusText =
      data.electionStatus as keyof typeof ElectionStatusEnum;
    return {
      electionEvent: data.electionEvent ? data.electionEvent : 'State Election',
      electionType: 'State Election',
      electionDate: new Date(data.electionDate),
      electionDateString: data.electionDate,
      electionStatus: electionStatusText
        ? ElectionStatusEnum[electionStatusText]
        : ElectionStatusEnum.final,
      electionStart: data.electionStart ? new Date(data.electionStart) : null,
      districts: data.districts,
    };
  }

  // ------------------------- HAStatic --------------------------

  public loadHAStatic$(electionDate: string): Observable<IHAStatic> {
    if (environment.isMock) {
      return this.http
        .get('assets/mock/HAStatic.json')
        .pipe(map((response: any) => this.mapToIHAStatic(response)));
    }
    return this.getCurrent$(`HAStatic/${electionDate}`).pipe(
      map((response: any) => this.mapToIHAStatic(response))
    );
  }

  private mapToIHAStatic(data: any): IHAStatic {
    return {
      electionType: 'State Election',
      electionDate: new Date(data.electionDate),
      prevElectionDate: new Date(data.prevElectionDate),
      districts: data.districts
        ? data.districts.map((item: any) => this.mapToIHAStaticDistrict(item))
        : [],
    };
  }

  private mapToIHAStaticDistrict(data: any): IHAStaticDistrict {
    return {
      districtName: data.districtName,
      profileId: data.profileId ? data.profileId : '',
      districtEnrolled: +data.districtEnrolled,
      pollingPlaces: data.pollingPlaces
        ? data.pollingPlaces.map((item: any) =>
            this.mapToIHAStaticPollingPlace(item)
          )
        : [],
      candidates: data.candidates
        ? data.candidates.map((item: any) => this.mapToIHAStaticCandidate(item))
        : [],
    };
  }

  private mapToIHAStaticPollingPlace(data: any): IHAStaticPollingPlace {
    return {
      pollingPlaceName: data.pollingPlaceName,
      locationAddress: data.locationAddress,
      coordinates: data.coordinates,
      pollingPlaceType: data.pollingPlaceType,
      accessibility: data.accessibility,
    };
  }

  private mapToIHAStaticCandidate(data: any): IHAStaticCandidate {
    return {
      candidateId: data.candidateId,
      partyId: data.partyId,
      partyName: data.partyName,
      colourCode: data.colourCode,
      candidateName: data.candidateName,
      gender: data.gender ? data.gender : '',
    };
  }

  // ------------------------- getHAChange --------------------------

  public loadHAChange$(
    electionDate: string,
    version: number
  ): Observable<IHAChange> {
    if (environment.isMock) {
      return this.http
        .get('assets/mock/HAChange.json')
        .pipe(map((response: any) => this.mapToIHAChange(response)));
    }
    return this.getCurrent$(`HAChange/${electionDate}/${version}`).pipe(
      map((response: any) => this.mapToIHAChange(response))
    );
  }

  public loadHAChangeTest$(filename: string): Observable<IHAChange> {
    return this.http
      .get(`assets/mock/${filename}.json`)
      .pipe(map((response: any) => this.mapToIHAChange(response)));
  }

  private mapToIHAChange(data: any): IHAChange {
    const electionStatusText =
      data.electionStatus as keyof typeof ElectionStatusEnum;

    // Parse lastUpdated timestamp - fallback to electionDate if not provided
    let lastUpdate: Date;
    if (data.lastUpdated) {
      lastUpdate = new Date(data.lastUpdated);
    } else if (data.lastUpdate) {
      lastUpdate = new Date(data.lastUpdate);
    } else {
      // Use election date as fallback instead of current time
      lastUpdate = new Date(data.electionDate);
      console.warn(
        'HAChange: lastUpdated field missing, using electionDate as fallback'
      );
    }

    return {
      electionType: data.electionType ? data.electionType : 'State Election',
      lastUpdate: lastUpdate,
      electionDate: new Date(data.electionDate),
      electionStatus: electionStatusText
        ? ElectionStatusEnum[electionStatusText]
        : ElectionStatusEnum.final,
      dataVersion: +data.dataVersion,
      districts: data.districts
        ? data.districts.map((item: any) => this.mapToIHAChangeDistrict(item))
        : [],
    };
  }

  private mapToIHAChangeDistrict(data: any): IHAChangeDistrict {
    return {
      districtId: data.districtId,
      informalDeclarationVotes: +data.informalDeclarationVotes,
      distributionCountDate: data.distributionCountDate
        ? new Date(data.distributionCountDate)
        : null,
      pollingPlaces: data.pollingPlaces
        ? data.pollingPlaces.map((item: any) =>
            this.mapToIHAChangePollingPlace(item)
          )
        : [],
      candidates: data.candidates
        ? data.candidates.map((item: any) => this.mapToIHAChangeCandidate(item))
        : [],
      declarations: data.declarations
        ? data.declarations.map((item: any) => this.mapToIDeclaration(item))
        : [],
      absentOrdinary: data.absentOrdinary
        ? data.absentOrdinary.map((item: any) => this.mapToIAbsentOrdinary(item))
        : [],
      finalDistribution: data.finalDistribution
        ? data.finalDistribution.map((item: any) => this.mapToIFinalDistributionRound(item))
        : undefined,
    };
  }

  private mapToIFinalDistributionRound(data: any): IFinalDistributionRound {
    return {
      roundNumber: +data.roundNumber,
      roundType: data.roundType ?? '',
      excludedCandidateId: data.excludedCandidateId != null ? data.excludedCandidateId : null,
      excludedCandidateName: data.excludedCandidateName ?? ' ',
      excludedCandidateVotes: +data.excludedCandidateVotes || 0,
      candidateResults: (data.candidateResults ?? []).map((item: any) => ({
        candidateId: +item.candidateId,
        voteChange: +item.voteChange || 0,
        progressiveTotal: item.progressiveTotal != null ? +item.progressiveTotal : null,
        isExcluded: !!item.isExcluded,
        isElected: !!item.isElected,
        isDrawResult: !!item.isDrawResult,
        percentage: item.percentage != null ? +item.percentage : null,
      })),
    };
  }

  private mapToIHAChangePollingPlace(data: any): IHAChangePollingPlace {
    return {
      pollingPlaceName: data.pollingPlaceName,
      informalVotes: +data.informalVotes,
      pollingCandidates: data.pollingCandidates
        ? data.pollingCandidates.map((item: any) =>
            this.mapToIHAChangePollingCandidate(item)
          )
        : [],
    };
  }

  private mapToIHAChangePollingCandidate(data: any): IHAChangePollingCandidate {
    return {
      candidateId: data.candidateId,
      formalVotes: +data.formalVotes,
      twoCandidatePref: data.twoCandidatePref ? data.twoCandidatePref : null,
      twoPartyPref: data.twoPartyPref ? data.twoPartyPref : null,
    };
  }

  private mapToIHAChangeCandidate(data: any): IHAChangeCandidate {
    return {
      candidateId: data.candidateId,
      ordinaryVotes: +data.ordinaryVotes,
      declarationVotes: +data.declarationVotes,
      twoCandidatePrefDeclarationVotes:
        data.twoCandidatePrefDeclarationVotes !== undefined &&
        data.twoCandidatePrefDeclarationVotes !== null
          ? +data.twoCandidatePrefDeclarationVotes
          : null,
      twoPartyPrefDeclarationVotes:
        data.twoPartyPrefDeclarationVotes !== undefined &&
        data.twoPartyPrefDeclarationVotes !== null
          ? +data.twoPartyPrefDeclarationVotes
          : null,
      distributionVotes: data.distributionVotes
        ? data.distributionVotes.map((item: any) =>
            this.mapToIDistributionVote(item)
          )
        : null,
    };
  }

  private mapToIDeclaration(data: any): IDeclaration {
    return {
      declarationType: data.declarationType,
      formalVotes: +data.formalVotes || 0,
      informalVotes: +data.informalVotes || 0,
      candidateVotes: (data.candidateVotes || []).map((item: any) => this.mapToICandidateVote(item)),
    };
  }

  private mapToICandidateVote(data: any): ICandidateVote {
    const candidateId = data.candidateId != null ? +data.candidateId : (data.CandidateId != null ? +data.CandidateId : 0);
    const votes = data.votes != null ? +data.votes : (data.Votes != null ? +data.Votes : 0);
    // Different feeds use slightly different key names for the 2CP count.
    const rawTwoCp =
      data.twoCandidatePrefVotes ??
      data.TwoCandidatePrefVotes ??
      data.twoCandidatePrefVoteCount ??
      data.TwoCandidatePrefVoteCount;
    const twoCandidatePrefVotes =
      rawTwoCp !== undefined && rawTwoCp !== null ? +rawTwoCp : undefined;
    return {
      candidateId,
      candidateFullName: data.candidateFullName ?? data.CandidateFullName ?? '',
      partyName: data.partyName ?? data.PartyName ?? '',
      votes,
      votePercentage: data.votePercentage != null ? +data.votePercentage : (data.VotePercentage != null ? +data.VotePercentage : 0),
      twoCandidatePrefVotes,
    };
  }

  private mapToIAbsentOrdinary(data: any): IAbsentOrdinary {
    return {
      absentOrdinaryType: data.absentOrdinaryType ?? '',
      formalVotes: +data.formalVotes || 0,
      informalVotes: +data.informalVotes || 0,
      candidateVotes: (data.candidateVotes || []).map((item: any) => this.mapToICandidateVote(item)),
    };
  }

  private mapToIDistributionVote(data: any): IDistributionVote {
    return {
      round: +data.round,
      distributionVote: +data.distributionVote,
    };
  }

  // ------------------------- getLCStatic --------------------------

  public loadLCStatic$(electionDate: string): Observable<ILCStatic> {
    if (environment.isMock) {
      return this.http
        .get('assets/mock/LCStatic.json')
        .pipe(map((response: any) => this.mapToILCStatic(response)));
    }
    return this.getCurrent$(`LCStatic/${electionDate}`).pipe(
      map((response: any) => this.mapToILCStatic(response))
    );
  }

  private mapToILCStatic(data: any): ILCStatic {
    return {
      electionType: 'State Election',
      electionDate: new Date(data.electionDate),
      lcSeatsContested: data.lcSeatsContested,
      parties: data.parties
        ? data.parties.map((item: any) => this.mapToILCStaticParty(item))
        : [],
    };
  }

  private mapToILCStaticParty(data: any): ILCStaticParty {
    return {
      id: +data.id,
      partyName: data.partyName,
      partyId: data.partyId,
      colourCode: data.colourCode,
    };
  }

  // ------------------------- getLCChange --------------------------

  public loadLCChange$(
    electionDate: string,
    version: number
  ): Observable<ILCChange> {
    if (environment.isMock) {
      return this.http
        .get('assets/mock/LCChange.json')
        .pipe(
          map((response: any) => this.mapToILCChange(response)),
          map((lcChange: ILCChange) => this.enrichLCChangeWithFinalResults(lcChange, electionDate))
        );
    }
    return this.getCurrent$(`LCChange/${electionDate}/${version}`).pipe(
      map((response: any) => this.mapToILCChange(response)),
      map((lcChange: ILCChange) => this.enrichLCChangeWithFinalResults(lcChange, electionDate))
    );
  }

  private mapToILCChange(data: any): ILCChange {
    const electionStatusText =
      data.electionStatus as keyof typeof ElectionStatusEnum;

    // Parse lastUpdated timestamp - fallback to electionDate if not provided
    let lastUpdate: Date;
    if (data.lastUpdated) {
      lastUpdate = new Date(data.lastUpdated);
    } else if (data.lastUpdate) {
      lastUpdate = new Date(data.lastUpdate);
    } else {
      // Use election date as fallback instead of current time
      lastUpdate = new Date(data.electionDate);
      console.warn(
        'LCChange: lastUpdated field missing, using electionDate as fallback'
      );
    }

    return {
      electionType: data.electionType ? data.electionType : 'State Election',
      lastUpdate: lastUpdate,
      electionDate: new Date(data.electionDate),
      electionStatus: electionStatusText
        ? ElectionStatusEnum[electionStatusText]
        : ElectionStatusEnum.final,
      dataVersion: +data.dataVersion,
      parties: data.parties
        ? data.parties.map((item: any) => this.mapToILCChangeParty(item))
        : [],
      pollingPlacesCounted: data.pollingPlacesCounted
        ? +data.pollingPlacesCounted
        : 0,
      totalPollingPlaces: data.totalPollingPlaces
        ? +data.totalPollingPlaces
        : 0,
      totalFormalVotes: data.totalFormalVotes ? +data.totalFormalVotes : 0,
      totalInformalVotes: data.totalInformalVotes
        ? +data.totalInformalVotes
        : 0,
      previousElectionResults: data.previousElectionResults,
    };
  }

  private mapToILCChangeParty(party: any): ILCChangeParty {
    return {
      id: party.id,
      votes: party.votes,
      seats: party.seats,
      candidates: party.candidates
        ? party.candidates.map((item: any) =>
            this.mapToILCChangeCandidate(item)
          )
        : [],
    };
  }

  private mapToILCChangeCandidate(candidate: any): ILCChangeCandidate {
    return {
      name: candidate.name,
      orderElected: candidate.orderElected,
      countNumber: candidate.countNumber,
      reElected: candidate.reElected,
      votes: candidate.votes,
    };
  }

  /**
   * Enrich LC Change data with final results from CSV when election is final
   * This ensures seats won data is available even if API doesn't provide it
   */
  private enrichLCChangeWithFinalResults(lcChange: ILCChange, electionDate: string): ILCChange {
    // Only enrich if election status is final
    if (lcChange.electionStatus !== ElectionStatusEnum.final) {
      return lcChange;
    }

    // Check if parties already have complete seats and candidate data
    const hasCompleteData = lcChange.parties.every(party => 
      party.seats != null && 
      party.seats > 0 && 
      party.candidates && 
      party.candidates.length > 0
    );

    // If we already have complete data, no need to enrich
    if (hasCompleteData) {
      return lcChange;
    }

    // Load and parse the CSV file synchronously (note: this is a simplified approach)
    // In production, you might want to handle this differently
    console.log('Enriching LC Change data with final results from CSV for election:', electionDate);

    // For now, return the original data - the actual enrichment will happen 
    // when the CSV service loads the data asynchronously
    return lcChange;
  }

  // ------------------------- LGEEvent --------------------------

  public loadLGEEvent$(): Observable<ILGEEvent[]> {
    return this.getCurrent$(`LGEEvents`).pipe(
      map((response: any) =>
        response.map((item: any) => this.mapToILGEEvent(item))
      )
    );
  }

  private mapToILGEEvent(data: any): ILGEEvent {
    return {
      electionDate: data.electionDate,
      electionName: data.electionName,
      electionStatus: data.electionStatus,
      nominationsClose: data.nominationsClose ? data.nominationsClose : '',
      isDefault: data.isDefault,
      showCouncils: data.isDefault,
    };
  }

  // ------------------------- LGEChange --------------------------

  public loadLGEChange$(
    electionDate: string,
    version: number
  ): Observable<ILGEChange> {
    if (environment.isMock) {
      return this.http
        .get('assets/mock/LGEChange.json')
        .pipe(map((response: any) => this.mapToILGEChange(response)));
    }
    return this.getCurrent$(`LGEChange/${electionDate}/${version}`).pipe(
      map((response: any) => {
        if (response !== null) {
          return this.mapToILGEChange(response);
        }
        // For null response, use electionDate parameter as fallback
        const fallbackDate = electionDate
          ? new Date(electionDate)
          : new Date('2020-01-01T00:00:00');
        return {
          uid: '',
          electionDate: fallbackDate,
          lastUpdate: fallbackDate,
          electionName: '',
          electionStatus: ElectionStatusEnum.precount,
          dataVersion: 0,
          councils: [],
        };
      })
    );
  }

  private mapToILGEChange(data: any): ILGEChange {
    // const electionStatusText = data.electionStatus ? data.electionStatus as keyof typeof ElectionStatusEnum : null;

    // Parse lastUpdate timestamp - fallback to electionDate if not provided
    let lastUpdate: Date;
    if (data.lastUpdate) {
      lastUpdate = new Date(data.lastUpdate);
    } else if (data.lastUpdated) {
      lastUpdate = new Date(data.lastUpdated);
    } else {
      // Use election date as fallback instead of current time
      lastUpdate = new Date(data.electionDate);
      console.warn(
        'LGEChange: lastUpdate field missing, using electionDate as fallback'
      );
    }

    const x = {
      uid: data.uid,
      electionDate: new Date(data.electionDate),
      lastUpdate: lastUpdate,
      electionName: data.electionName ? data.electionName : '',
      electionStatus: '', // electionStatusText ? ElectionStatusEnum[electionStatusText] : ElectionStatusEnum.final,
      dataVersion: data.dataVersion ? +data.dataVersion : 0,
      councils: data.councils
        ? data.councils.map((item: any) => this.mapToILGEChangeCouncil(item))
        : [],
    };
    return x;
  }

  private mapToILGEChangeCouncil(data: any): ILGEChangeCouncil {
    return {
      uid: data.uid,
      elections: data.elections
        ? data.elections.map((item: any) => this.mapToILGEChangeElection(item))
        : [],
    };
  }

  private mapToILGEChangeElection(data: any): ILGEChangeElection | null {
    if (data === null) {
      return null;
    }
    // if (data.uid === 'elections/38f47fff-a54f-438b-a3ec-7ac0c8970d1e') {
    //   console.log('here', data);
    // }

    const electionResultStatus =
      data.electionStatus as keyof typeof LGEResultsStatusEnum;
    return {
      uid: data.uid,
      electionResultsStatus: electionResultStatus
        ? LGEResultsStatusEnum[electionResultStatus]
        : LGEResultsStatusEnum.noResults,
      informalBallotCount: +data.informalBallotCount,
      totalEnrollment: +data.totalEnrollment,
      scrutinySheetUrl: data.scrutinySheetUrl,
      quota: +data.quota,
      declarationOn: data.declarationOn ? new Date(data.declarationOn) : null,
      candidates: data.candidates
        ? data.candidates.map((item: any) =>
            this.mapToILGEChangeCandidate(item)
          )
        : [],
    };
  }

  private mapToILGEChangeCandidate(data: any): ILGEChangeCandidate {
    return {
      uid: data.uid,
      firstPrefVotes: +data.firstPrefVotes,
      isElected: data.isElected,
      orderOfElection: +data.orderOfElection,
      votesAtConclusion: +data.votesAtConclusion,
      countNo: +data.countNo,
      documents: data.documents
        ? data.documents.map((item: any) => this.mapToILGEDocument(item))
        : [],
    };
  }

  private mapToILGEDocument(data: any): ILGEDocument {
    return {
      uid: data.uid,
      documentType: data.documentType,
      fileType: data.fileType,
      documentDescription: data.documentDescription,
      documentUrl: data.documentUrl,
      createdOn: data.createdOn ? new Date(data.createdOn) : new Date(),
    };
  }

  // ------------------------- LGEStatic --------------------------

  public loadLGEStatic$(
    electionDate: string,
    version: number
  ): Observable<ILGEStatic> {
    if (environment.isMock) {
      return this.http
        .get('assets/mock/LGEStatic.json')
        .pipe(map((response: any) => this.mapToILGEStatic(response)));
    }
    return this.getCurrent$(`LGEStatic/${electionDate}/${version}`).pipe(
      map((response: any) => this.mapToILGEStatic(response))
    );
  }

  private mapToILGEStatic(data: any): ILGEStatic {
    if (data === null) {
      // For null data, use a fixed historical date instead of current time
      const fallbackDate = new Date('2020-01-01T00:00:00');
      return {
        uid: 'electionEvents/',
        electionDate: fallbackDate,
        lastUpdate: fallbackDate,
        electionName: '',
        electionStatus: '',
        dataVersion: 0,
        councils: [],
      };
    }

    const councilResults: ILGEStaticCouncil[] = [];
    data.councils.forEach((council: any) => {
      if (
        council &&
        council.elections &&
        council.elections.some((election: any) => election !== null)
      ) {
        councilResults.push(this.mapToILGEStaticCouncil(council));
      }
    });

    // Parse lastUpdate timestamp - fallback to electionDate if not provided
    let lastUpdate: Date;
    if (data.lastUpdate) {
      lastUpdate = new Date(data.lastUpdate);
    } else if (data.lastUpdated) {
      lastUpdate = new Date(data.lastUpdated);
    } else {
      // Use election date as fallback instead of current time
      lastUpdate = new Date(data.electionDate);
      console.warn(
        'LGEStatic: lastUpdate field missing, using electionDate as fallback'
      );
    }

    return {
      uid: data.uid,
      electionDate: new Date(data.electionDate),
      lastUpdate: lastUpdate,
      electionName: data.electionName,
      electionStatus: data.electionStatus,
      dataVersion: +data.dataVersion,
      councils: councilResults, // data.councils ? data.councils.map((item: any) => this.mapToILGEStaticCouncil(item)) : []
    };
  }

  private mapToILGEStaticCouncil(data: any): ILGEStaticCouncil {
    const electionResulte: ILGEStaticElection[] = [];
    data.elections.forEach((election: any) => {
      if (election !== null) {
        electionResulte.push(this.mapToILGEStaticElection(election));
      }
    });

    return {
      uid: data.uid,
      councilName: data.councilName,
      councilFullName: data.councilFullName,
      councilCode: data.councilCode,
      councilLogoURL: data.councilLogoURL,
      electionStatus: data.electionStatus,
      elections: electionResulte, // data.elections ? data.elections.map((item: any) => this.mapToILGEStaticElection(item)) : []
    };
  }

  private mapToILGEStaticElection(data: any): ILGEStaticElection {
    const electionStatus =
      data.electionStatus as keyof typeof LGEElectionStatusEnum;
    return {
      uid: data.uid,
      councilUid: data.councilUid,
      electionName: data.electionName,
      electionCode: data.electionCode,
      electionLine: data.electionLine,
      electionStatus: electionStatus
        ? LGEElectionStatusEnum[electionStatus]
        : LGEElectionStatusEnum.notPublic,
      vacancies: +data.vacancies,
      candidates: data.candidates
        ? data.candidates.map((item: any) =>
            this.mapToILGEStaticCandidate(item)
          )
        : [],
    };
  }

  private mapToILGEStaticCandidate(data: any): ILGEStaticCandidate {
    return {
      uid: data.uid,
      candidateOrder: +data.candidateOrder,
      ballotName: data.ballotName,
      candidatePhotoUrl: data.candidatePhotoUrl,
      photoBrightness:
        data.photoBrightness === undefined ? 125 : +data.photoBrightness,
      contactDetails: data.contactDetails,
      liveInTheArea: data.liveInTheArea,
      partyAffiliation: data.partyAffiliation,
      profileText: data.profileText,
      documents: data.documents
        ? data.documents.map((item: any) => this.mapToILGEDocument(item))
        : [],
    };
  }

  // ------------------------- get Image --------------------------

  public getImageFromBlob$(url: string): Observable<any> {
    //  console.log(environment.currentData + `api/Image/DownloadBlob/`)
    return this.http.get(environment.currentData + `api/Image/DownloadBlob`, {
      observe: 'body',
      responseType: 'blob',
      params: { uri: url },
    });
  }

  // ----------------------- get Document -------------------------

  public downloadDocumentBlob$(url: string): Observable<any> {
    //  console.log(environment.currentData + `api/Document/DownloadBlob/`)
    return this.http.get(environment.currentData + `api/Image/DownloadBlob`, {
      observe: 'body',
      responseType: 'blob',
      params: { uri: url },
    });
  }
}
